import React from 'react';
import { cn } from '@hero/web-lib';

export interface SelectableCardProps {
  selected: boolean;
  onClick: () => void;
  title: string;
  description?: string;
  /** Optional leading icon. */
  icon?: React.ReactNode;
  /** When true, replaces the right-side circle indicator with nothing
   * (used when the card is the only selectable in its row). */
  hideIndicator?: boolean;
  className?: string;
}

/**
 * Selectable card used by the unified signup wizard. Visually matches
 * the carrier-dashboard register screen (border becomes primary blue
 * when selected, with a radio-style indicator on the trailing edge).
 *
 * Logical CSS utilities (`text-start`, `ms-`, `me-`) keep RTL working
 * automatically — see `.cursorrules` "RTL / LTR Support".
 */
export function SelectableCard({
  selected,
  onClick,
  title,
  description,
  icon,
  hideIndicator = false,
  className,
}: SelectableCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-3 text-start transition-colors',
        selected
          ? 'border-primary bg-primary/5'
          : 'border-gray-300 bg-white hover:border-gray-400',
        className,
      )}>
      <span className="flex min-w-0 flex-1 items-center gap-3">
        {icon ? (
          <span
            className={cn(
              'flex size-10 flex-shrink-0 items-center justify-center rounded-full',
              selected ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500',
            )}>
            {icon}
          </span>
        ) : null}
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className={cn(
              'text-sm font-semibold leading-5',
              selected ? 'text-primary' : 'text-gray-900',
            )}>
            {title}
          </span>
          {description ? (
            <span className="mt-0.5 text-xs leading-4 text-gray-500">{description}</span>
          ) : null}
        </span>
      </span>
      {!hideIndicator ? (
        <span
          aria-hidden
          className={cn(
            'relative size-5 flex-shrink-0 rounded-full border-2',
            selected ? 'border-primary' : 'border-gray-400',
          )}>
          {selected ? (
            <span className="absolute inset-0 m-auto size-2.5 rounded-full bg-primary" />
          ) : null}
        </span>
      ) : null}
    </button>
  );
}
